import { t } from 'utils/getTranslations';
import { compositeFromArgs, compositeSummary, unquoted } from '../compositeFilter';
import { intervalBound, rsqlifyMainFilter } from '../filterUtil';
import {
    FilterCheckboxInForm,
    FilterConfig,
    MainFilterForm,
    MainFilterInForm,
    NOT_NULL_FILTER_SENTINEL,
    NULL_FILTER_SENTINEL,
    QuickFilterDropdownMultiselectForm,
    QuickFilterForm,
    QuickFilterFormItem,
    TextMatch,
} from '../ListTableTypes';

/** Both filter state machines, as the list's filter hook holds them. */
export type FilterState = { mainFilterState: MainFilterForm; quickFilterState: QuickFilterForm };

export type FilterCondition = 'is' | 'contains' | 'is-not' | 'is-empty' | 'is-not-empty';

/** Each condition's message id, as the switch, the chips and a saved view's sentence name it. */
export const CONDITION_LABEL_IDS: Record<FilterCondition, string> = {
    is: 'filters.is',
    contains: 'filters.contains',
    'is-not': 'filters.is-not',
    'is-empty': 'filters.is-empty',
    'is-not-empty': 'filters.is-not-empty',
};

/**
 * One field of the filter bar: a main filter, or a quick filter no main filter shares a key with —
 * a shared key is the main filter's, which owns its clause (see `serializeFilterParams`).
 */
export type FilterField =
    | { kind: 'main'; key: string; filter: MainFilterInForm }
    | { kind: 'quick'; key: string; filter: QuickFilterFormItem };

export type FilterFieldGroup = {
    /** The drawer's accordion title, an i18n key; the drawer's ungrouped filters have none. */
    title?: string;
    fields: FilterField[];
};

export type FilterChip = {
    field: FilterField;
    label: string;
    /** "contains" reads as "is": the chip names the typed text, as every text chip does. */
    condition: Exclude<FilterCondition, 'contains'>;
    /** The chosen values' labels; `''` stands for an entity whose label has not been fetched yet. */
    values: string[];
    /** Set on a yes / no field: Yes reads as the label alone, No as "‹label›: No". */
    answer?: 'yes' | 'no';
    /** The answer's own name, read in place of either, on a field of `namedAnswers`. */
    answerLabel?: string;
    /** Set on a text field that offers `matchModes`: the chip reads "contains any of" or "contains all of". */
    match?: TextMatch;
    /** The whole text, for a chip of several parts that may truncate. */
    title?: string;
};

export type MultiselectForm = Extract<MainFilterInForm, { type: 'multiselect' | 'dynamicmultiselect' }>;

const isMultiselect = (filter: MainFilterInForm | QuickFilterFormItem): filter is MultiselectForm =>
    'type' in filter && (filter.type === 'multiselect' || filter.type === 'dynamicmultiselect');

const isQuickDropdown = (filter: QuickFilterFormItem): filter is QuickFilterDropdownMultiselectForm =>
    'type' in filter && filter.type === 'dropdown-multiselect';

/** The checkboxes of a checkbox-group field, main or quick; undefined for every other control. */
const checkboxGroupOf = (
    field: FilterField
): { options: FilterCheckboxInForm[]; multiple?: boolean; falseIsYes?: boolean } | undefined => {
    if (field.kind === 'main') {
        return field.filter.type === 'checkboxgroup'
            ? { options: field.filter.filters, multiple: field.filter.multiple, falseIsYes: field.filter.falseIsYes }
            : undefined;
    }
    return isQuickDropdown(field.filter)
        ? undefined
        : { options: field.filter.filters, multiple: field.filter.multiple, falseIsYes: field.filter.falseIsYes };
};

type EmptyableForm = Extract<MainFilterInForm, { type: 'text' | 'daterange' }>;

const isEmptyable = (filter: MainFilterInForm | QuickFilterFormItem): filter is EmptyableForm =>
    'type' in filter && (filter.type === 'text' || filter.type === 'daterange');

const holdsValue = (filter: EmptyableForm) =>
    filter.type === 'text' ? !!filter.value?.length : !!(filter.from || filter.to);

/**
 * The conditions a field offers, in the switch's order: "is not" needs `canExclude` or `conditions.not`,
 * "is empty" a `nullOption` or `conditions.empty`, "is not empty" a `notEmptyOption`; a text field matches by
 * "contains", and offers both emptiness conditions under an `emptyComparator`.
 */
export const conditionsFor = (filter: MainFilterInForm | QuickFilterFormItem): FilterCondition[] => {
    const conditions = 'conditions' in filter ? filter.conditions : undefined;
    if ('type' in filter && filter.type === 'composite') return filter.canExclude ? ['is', 'is-not'] : ['is'];
    if (isEmptyable(filter)) {
        const byComparator = filter.type === 'text' && !!filter.emptyComparator;
        return [
            filter.type === 'text' ? 'contains' : 'is',
            ...(conditions?.empty || byComparator ? (['is-empty'] as const) : []),
            ...(byComparator ? (['is-not-empty'] as const) : []),
        ];
    }
    if (!isMultiselect(filter)) return ['is'];
    return [
        'is',
        ...(filter.canExclude || conditions?.not ? (['is-not'] as const) : []),
        ...(filter.nullOption || conditions?.empty ? (['is-empty'] as const) : []),
        ...(filter.notEmptyOption ? (['is-not-empty'] as const) : []),
    ];
};

/**
 * The condition a filter's state expresses: exclusion is "is not", the null option alone is "is empty", as
 * is a text field or date range marked `isEmpty` while it holds no value, and the not-empty option "is not empty".
 */
export const conditionOf = (filter: MainFilterInForm | QuickFilterFormItem): FilterCondition => {
    if (isEmptyable(filter)) {
        if (filter.isEmpty && !holdsValue(filter)) return 'is-empty';
        if (filter.type === 'text' && filter.isNotEmpty && !holdsValue(filter)) return 'is-not-empty';
        return filter.type === 'text' ? 'contains' : 'is';
    }
    if ('type' in filter && filter.type === 'composite') return filter.isExcluding ? 'is-not' : 'is';
    if (!isMultiselect(filter)) return 'is';
    if (filter.isExcluding) return 'is-not';
    const { selected } = filter;
    if (selected.some((option) => option.value === NOT_NULL_FILTER_SENTINEL)) return 'is-not-empty';
    return selected.length > 0 && selected.every((option) => option.value === NULL_FILTER_SENTINEL) ? 'is-empty' : 'is';
};

/**
 * The filter switched to `condition`, as the drawer's exclude checkbox and null option would leave it; a text
 * field or date range going "is empty", or a text field going "is not empty", drops its value.
 */
export const withCondition = <F extends MainFilterInForm>(filter: F, condition: FilterCondition): F => {
    if (isEmptyable(filter)) {
        if (condition === 'is-not-empty') return { ...(clearedMainFilter(filter) as F), isNotEmpty: true };
        if (condition !== 'is-empty') return { ...filter, isEmpty: false, isNotEmpty: false };
        return { ...(clearedMainFilter(filter) as F), isEmpty: true };
    }
    if (filter.type === 'composite') return { ...filter, isExcluding: condition === 'is-not' };
    if (!isMultiselect(filter)) return filter;
    if (condition === 'is-not-empty') {
        return {
            ...filter,
            isExcluding: false,
            selected: [{ value: NOT_NULL_FILTER_SENTINEL, label: filter.notEmptyOption?.label ?? '' }],
        };
    }
    if (condition === 'is-empty') {
        return {
            ...filter,
            isExcluding: false,
            selected: [{ value: NULL_FILTER_SENTINEL, label: filter.nullOption?.label ?? '' }],
        };
    }
    return {
        ...filter,
        isExcluding: condition === 'is-not',
        selected: filter.selected.filter(
            (option) => option.value !== NULL_FILTER_SENTINEL && option.value !== NOT_NULL_FILTER_SENTINEL
        ),
    };
};

/**
 * A checkbox group read as one question: its single option is Yes, or its `true` / `false` pair is Yes
 * and No, the other way round on a `falseIsYes` group. A group that may hold several options at once is
 * a multi-select instead.
 */
export const yesNoOptions = (
    field: FilterField
): { yes: FilterCheckboxInForm; no?: FilterCheckboxInForm } | undefined => {
    const group = checkboxGroupOf(field);
    if (!group) return undefined;
    const answers = group.options.filter((option) => !option.isNo);
    if (answers.length === 1) return { yes: answers[0], no: group.options.find((option) => option.isNo) };
    const [yesArgument, noArgument] = group.falseIsYes ? ['false', 'true'] : ['true', 'false'];
    const yes = group.options.find((option) => option.argument === yesArgument);
    const no = group.options.find((option) => option.argument === noArgument);
    return group.options.length === 2 && group.multiple !== true && yes && no ? { yes, no } : undefined;
};

/**
 * A checkbox group without a label of its own is named by its options, as its checkboxes read today. A quick
 * checkbox group's `label` and `chipLabel` are i18n keys; every other label comes translated.
 */
export const fieldLabel = (field: FilterField): string => {
    const options = checkboxGroupOf(field)?.options.filter((option) => !option.isNo);
    if (field.kind === 'quick' && !isQuickDropdown(field.filter)) {
        const key = field.filter.label || field.filter.chipLabel;
        if (key) return t(key);
    }
    return field.filter.label || options?.map((option) => option.label).join(' / ') || field.filter.selector;
};

const isFieldActive = (field: FilterField, mainFilterState: MainFilterForm): boolean => {
    if (field.kind === 'main') {
        const clause = rsqlifyMainFilter(field.filter, mainFilterState);
        return !!(clause?.realFilter || clause?.virtualFilter);
    }
    return isQuickDropdown(field.filter)
        ? field.filter.selected.length > 0
        : field.filter.filters.some((option) => option.isSelected);
};

const isHidden = (field: FilterField, { mainFilterState, quickFilterState }: FilterState): boolean =>
    field.kind === 'main' &&
    field.filter.getShouldHide?.({
        mainFilters: mainFilterState,
        quickFilters: quickFilterState,
        groupSelector: field.key,
    }) === true;

const fieldsOf = ({ mainFilterState, quickFilterState }: FilterState) => ({
    main: Object.entries(mainFilterState).map(([key, filter]): FilterField => ({ kind: 'main', key, filter })),
    quickOnly: Object.entries(quickFilterState)
        .filter(([key]) => !mainFilterState[key])
        .map(([key, filter]): FilterField => ({ kind: 'quick', key, filter })),
});

/**
 * The fields + Filter offers, in the drawer's sections and order: its ungrouped filters first, then
 * one section per `group`. Quick-only filters lead the first section; hidden filters are left out.
 */
export const filterFieldGroups = (state: FilterState): FilterFieldGroup[] => {
    const { main, quickOnly } = fieldsOf(state);
    const ungrouped: FilterField[] = [];
    const grouped = new Map<string, FilterField[]>();
    main.filter((field) => !isHidden(field, state)).forEach((field) => {
        const { group } = field.filter as MainFilterInForm;
        if (group) {
            grouped.set(group, [...(grouped.get(group) ?? []), field]);
        } else {
            ungrouped.push(field);
        }
    });
    const groups: FilterFieldGroup[] = [
        ...(ungrouped.length ? [{ fields: ungrouped }] : []),
        ...Array.from(grouped, ([title, fields]) => ({ title, fields })),
    ];
    if (!quickOnly.length) return groups;
    const [first, ...rest] = groups;
    return first ? [{ ...first, fields: [...quickOnly, ...first.fields] }, ...rest] : [{ fields: quickOnly }];
};

const range = (from: string, to: string) => [from, to].join(' – ').trim();

const valuesOf = (field: FilterField, condition: FilterCondition, formatDate: (value: string) => string): string[] => {
    if (condition === 'is-empty' || condition === 'is-not-empty') return [];
    if (field.kind === 'quick') {
        const { filter } = field;
        return isQuickDropdown(filter)
            ? filter.selected.map((option) => option.label)
            : filter.filters.filter((option) => option.isSelected).map((option) => option.label);
    }
    const { filter } = field;
    switch (filter.type) {
        case 'checkboxgroup':
            return filter.filters.filter((option) => option.isSelected).map((option) => option.label);
        case 'multiselect':
        case 'dynamicmultiselect':
            return filter.selected.map((option) =>
                option.value === NULL_FILTER_SENTINEL ? (filter.nullOption?.label ?? '') : option.label
            );
        case 'dropdown':
        case 'dynamicdropdown':
            return filter.selected ? [filter.selected.label] : [];
        case 'text':
            return Array.isArray(filter.value) ? filter.value : filter.value ? [filter.value] : [];
        case 'date':
        case 'offset-date-time':
            return filter.value ? [formatDate(filter.value)] : [];
        case 'number':
            return filter.value != null ? [String(filter.value)] : [];
        case 'daterange':
        case 'offset-date-time-range':
            return [range(filter.from ? formatDate(filter.from) : '', filter.to ? formatDate(filter.to) : '')];
        case 'interval':
            return [range(intervalBound(filter.from), intervalBound(filter.to))];
        case 'composite':
            return [compositeSummary(filter, formatDate).join(' · ')];
    }
};

/** One chip per field that sends a clause, read from the filter state rather than the URL text. */
export const filterChips = (state: FilterState, formatDate: (value: string) => string): FilterChip[] => {
    const { main, quickOnly } = fieldsOf(state);
    return [...main, ...quickOnly]
        .filter((field) => isFieldActive(field, state.mainFilterState))
        .map((field): FilterChip => {
            const label = fieldLabel(field);
            const yesNo = yesNoOptions(field);
            if (yesNo) {
                const picked = yesNo.yes.isSelected ? yesNo.yes : yesNo.no;
                const named = 'namedAnswers' in field.filter && field.filter.namedAnswers;
                return {
                    field,
                    label,
                    condition: 'is',
                    values: [],
                    answer: yesNo.yes.isSelected ? 'yes' : 'no',
                    ...(named && picked ? { answerLabel: picked.label } : {}),
                };
            }
            const condition = conditionOf(field.filter);
            const values = valuesOf(field, condition, formatDate);
            const match =
                field.kind === 'main' &&
                field.filter.type === 'text' &&
                field.filter.matchModes &&
                condition === 'contains'
                    ? (field.filter.match ?? 'any')
                    : undefined;
            const title =
                field.kind === 'main' && field.filter.type === 'composite'
                    ? `${label}: ${compositeSummary(field.filter, formatDate).join(', ')}`
                    : undefined;
            return { field, label, condition: condition === 'contains' ? 'is' : condition, values, match, title };
        });
};

/** The values a main filter of fixed options holds; undefined for every other control, and for an "is not" one. */
const mainValuesOf = (filter: MainFilterInForm): string[] | undefined => {
    switch (filter.type) {
        case 'checkboxgroup':
            return filter.filters.some((option) => option.isSelected && option.isNo)
                ? undefined
                : filter.filters.filter((option) => option.isSelected).map((option) => option.argument);
        case 'multiselect':
            return filter.isExcluding ? undefined : filter.selected.map((option) => unquoted(option.value));
        case 'dropdown':
            return filter.selected ? [unquoted(filter.selected.value)] : [];
        default:
            return undefined;
    }
};

/**
 * Whether the quick filter on a main filter's key already shows what the main filter holds: every value the main
 * filter has is ticked on the quick filter, which the list keeps in step with it. Such a main filter needs no chip
 * of its own beside the quick-filter chips.
 */
export const isShownByQuickFilter = (field: FilterField, quickFilterState: QuickFilterForm): boolean => {
    const quick = quickFilterState[field.key];
    if (field.kind !== 'main' || !quick) return false;
    const values = mainValuesOf(field.filter);
    if (!values?.length) return false;
    const ticked = new Set(
        isQuickDropdown(quick)
            ? quick.selected.map((option) => unquoted(option.value))
            : quick.filters.filter((option) => option.isSelected).map((option) => option.argument)
    );
    return values.every((value) => ticked.has(value));
};

/**
 * A copy of the state the + Filter popover can edit: the drawer's controls write into the filter
 * objects they are handed, and the popover's edits must not reach the list before Apply.
 */
export const cloneFilterState = ({ mainFilterState, quickFilterState }: FilterState): FilterState => {
    const cloneEntry = <T extends object>(entry: T): T => {
        const copy = { ...entry } as Record<string, unknown>;
        if (Array.isArray(copy.selected)) copy.selected = [...copy.selected];
        if (Array.isArray(copy.filters)) copy.filters = (copy.filters as object[]).map((option) => ({ ...option }));
        return copy as T;
    };
    const cloneForm = <T extends object>(form: Record<string, T>) =>
        Object.fromEntries(Object.entries(form).map(([key, entry]) => [key, cloneEntry(entry)]));
    return { mainFilterState: cloneForm(mainFilterState), quickFilterState: cloneForm(quickFilterState) };
};

/** The main filter holding no value, so it sends no clause. */
export const clearedMainFilter = (filter: MainFilterInForm): MainFilterInForm => {
    switch (filter.type) {
        case 'checkboxgroup':
            return { ...filter, filters: filter.filters.map((option) => ({ ...option, isSelected: false })) };
        case 'multiselect':
        case 'dynamicmultiselect':
            return { ...filter, selected: [], isExcluding: false };
        case 'dropdown':
        case 'dynamicdropdown':
            return { ...filter, selected: null };
        case 'text':
            return { ...filter, value: null, isEmpty: false, isNotEmpty: false };
        case 'date':
        case 'offset-date-time':
        case 'number':
            return { ...filter, value: null };
        case 'daterange':
            return { ...filter, from: null, to: null, isEmpty: false };
        case 'offset-date-time-range':
        case 'interval':
            return { ...filter, from: null, to: null };
        case 'composite':
            return compositeFromArgs(filter);
    }
};

export const clearedQuickFilter = (filter: QuickFilterFormItem): QuickFilterFormItem =>
    isQuickDropdown(filter)
        ? { ...filter, selected: [] }
        : { ...filter, filters: filter.filters.map((option) => ({ ...option, isSelected: false })) };

/** The state without `field`'s clause; a quick filter on the same key goes with the main filter. */
export const withoutField = (state: FilterState, field: FilterField): FilterState => {
    const quick = state.quickFilterState[field.key];
    return {
        mainFilterState:
            field.kind === 'main'
                ? { ...state.mainFilterState, [field.key]: clearedMainFilter(field.filter) }
                : state.mainFilterState,
        quickFilterState: quick
            ? { ...state.quickFilterState, [field.key]: clearedQuickFilter(quick) }
            : state.quickFilterState,
    };
};

/** Values left on filters hidden by `getShouldHide`, cleared the way the drawer's controls clear them. */
export const withoutHiddenValues = (state: FilterState): FilterState => ({
    ...state,
    mainFilterState: Object.fromEntries(
        Object.entries(state.mainFilterState).map(([key, filter]) => [
            key,
            !filter.preserveValueWhenHidden && isHidden({ kind: 'main', key, filter }, state)
                ? clearedMainFilter(filter)
                : filter,
        ])
    ),
});

/** How many values a list-like field holds, for the popover's "N selected"; undefined for the rest. */
export const selectedCount = (field: FilterField): number | undefined => {
    const group = checkboxGroupOf(field);
    if (group) return group.options.filter((option) => option.isSelected).length;
    if (field.kind === 'quick') return isQuickDropdown(field.filter) ? field.filter.selected.length : undefined;
    if (field.filter.type === 'composite') {
        const list = field.filter.parts.find((part) => part.type === 'multiselect');
        return list?.type === 'multiselect' ? list.selected.length : undefined;
    }
    return isMultiselect(field.filter)
        ? field.filter.selected.filter((option) => option.value !== NULL_FILTER_SENTINEL).length
        : undefined;
};

/**
 * A clause's selector: `matterStateTypeCode` of `matterStateTypeCode=in=(A,B)`, and of a grouped
 * `(office.id=out=(1),office.id=isnull=true)`. The operator is left out, so a field switched from "is" to "is not"
 * or "is empty" (`=in=` to `=out=` or `=isnull=`) still reads as that field.
 */
const clauseSelector = (clause: string) => /^\(?([^=!<>(]+)/.exec(clause)?.[1] ?? clause;

/** Whether a `qf` / `mf` clause is one a field of `config` writes, read by its selector. */
export const configHoldsClause = (config: FilterConfig | undefined) => {
    const selectors = new Set(
        [...(config?.filters ?? []), ...(config?.quickFilters ?? [])].flatMap((filter) => [
            filter.selector,
            ...('jointFilter' in filter ? (filter.jointFilter?.selectors ?? []) : []),
            ...('no' in filter && filter.no ? [filter.no.selector] : []),
        ])
    );
    return (clause: string) => selectors.has(clauseSelector(clause));
};
